import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, SlidersHorizontal, ShoppingCart, Loader } from 'lucide-react';
import { SEARCH_URL, SEARCH_SUGGESTIONS_URL, CART_ADD_URL, CART_VIEW_URL } from '../../config/api';
import { getFirstImageUrl, authenticatedFetch } from '../Utils/apiHelpers';
import { trackFood } from '../Utils/analytics';
import './SearchBar.css';

interface Suggestion {
  type: string;
  food_id?: number;
  name: string;
  image?: string;
}

interface SearchFood {
  food_id: number;
  food_name: string;
  description?: string;
  price: number;
  category?: string;
  image_urls?: string | string[];
  average_rating?: number;
  available_quantity?: number;
  kitchen_name?: string;
  kitchen_id?: number;
}

interface SearchBarProps {
  onCartChange?: () => void;
}

const FALLBACK_IMG = 'https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg';

const SearchBar: React.FC<SearchBarProps> = ({ onCartChange }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchFood[] | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [addingId, setAddingId] = useState<number | null>(null);
  const isLoggedIn = !!localStorage.getItem('authToken');

  const [filters, setFilters] = useState({
    category: '',
    city: '',
    min_price: '',
    max_price: '',
    sort_by: '',
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) { setSuggestions([]); return; }
    try {
      const res = await fetch(`${SEARCH_SUGGESTIONS_URL}?q=${encodeURIComponent(q.trim())}`, {
        headers: { accept: 'application/json' },
      });
      if (!res.ok) return;
      const data = await res.json();
      setSuggestions((data.suggestions || []).slice(0, 6));
    } catch {
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(query);
      setShowSuggestions(true);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, fetchSuggestions]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setShowFilters(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const performSearch = async (q: string = query, overrideFilters = filters) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setIsSearching(true);
    setShowSuggestions(false);
    setShowFilters(false);

    const params = new URLSearchParams();
    if (trimmed) params.set('query', trimmed);
    if (overrideFilters.category) params.set('category', overrideFilters.category);
    if (overrideFilters.city) params.set('city', overrideFilters.city);
    if (overrideFilters.min_price) params.set('min_price', overrideFilters.min_price);
    if (overrideFilters.max_price) params.set('max_price', overrideFilters.max_price);
    if (overrideFilters.sort_by) params.set('sort_by', overrideFilters.sort_by);

    try {
      const res = await fetch(`${SEARCH_URL}?${params.toString()}`, {
        headers: { accept: 'application/json' },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setResults(data.foods || []);
      setShowResults(true);
      trackFood('search', trimmed);
    } catch {
      setResults([]);
      setShowResults(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') performSearch();
    if (e.key === 'Escape') { setShowSuggestions(false); setShowResults(false); }
  };

  const handleSuggestionClick = (s: Suggestion) => {
    // Search by food name to show all kitchens offering this food
    const foodName = s.name;
    setQuery(foodName);
    setShowSuggestions(false);
    performSearch(foodName);
  };

  const handleAddToCart = async (food: SearchFood) => {
    if (!isLoggedIn) return;
    setAddingId(food.food_id);
    try {
      const cartRes = await authenticatedFetch(CART_VIEW_URL);
      if (cartRes.ok) {
        const cartData = await cartRes.json();
        if (cartData.success && cartData.items?.length > 0) {
          const existingKitchen = cartData.items[0]?.kitchen_id;
          if (existingKitchen && food.kitchen_id && existingKitchen !== food.kitchen_id) {
            // Different kitchen — just skip silently; user can clear cart in cart drawer
            setAddingId(null);
            return;
          }
        }
      }
      const res = await authenticatedFetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ food_id: food.food_id, quantity: 1 }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        trackFood('add_to_cart', food.food_name);
        onCartChange?.();
      }
    } catch {
      // non-critical
    } finally {
      setAddingId(null);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setResults(null);
    setShowResults(false);
    setShowSuggestions(false);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="sb-container" ref={containerRef}>
      <div className="sb-input-row">
        <div className="sb-input-wrap">
          <Search size={16} className="sb-icon" />
          <input
            type="text"
            className="sb-input"
            placeholder="Search food, kitchens..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
            aria-label="Search"
          />
          {query && (
            <button className="sb-clear" onClick={clearSearch} aria-label="Clear">
              <X size={14} />
            </button>
          )}
        </div>

        <button
          className={`sb-filter-btn${activeFilterCount > 0 ? ' sb-filter-btn--active' : ''}`}
          onClick={() => setShowFilters(v => !v)}
          title="Filters"
        >
          <SlidersHorizontal size={16} />
          {activeFilterCount > 0 && <span className="sb-filter-badge">{activeFilterCount}</span>}
        </button>

        <button
          className="sb-search-btn"
          onClick={() => performSearch()}
          disabled={isSearching || !query.trim()}
        >
          {isSearching ? <span className="sb-spinner" /> : <Search size={16} />}
          <span className="sb-search-label">Search</span>
        </button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="sb-filters">
          <div className="sb-filters-grid">
            <div className="sb-filter-group">
              <label className="sb-filter-label">Category</label>
              <select
                className="sb-filter-input"
                value={filters.category}
                onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
              >
                <option value="">Any</option>
                <option value="veg">Vegetarian</option>
                <option value="non-veg">Non-Vegetarian</option>
              </select>
            </div>
            <div className="sb-filter-group">
              <label className="sb-filter-label">City</label>
              <input
                type="text"
                className="sb-filter-input"
                placeholder="e.g. Mumbai"
                value={filters.city}
                onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
              />
            </div>
            <div className="sb-filter-group">
              <label className="sb-filter-label">Min Price (₹)</label>
              <input
                type="number"
                className="sb-filter-input"
                min="0"
                placeholder="0"
                value={filters.min_price}
                onChange={e => setFilters(f => ({ ...f, min_price: e.target.value }))}
              />
            </div>
            <div className="sb-filter-group">
              <label className="sb-filter-label">Max Price (₹)</label>
              <input
                type="number"
                className="sb-filter-input"
                min="0"
                placeholder="∞"
                value={filters.max_price}
                onChange={e => setFilters(f => ({ ...f, max_price: e.target.value }))}
              />
            </div>
            <div className="sb-filter-group">
              <label className="sb-filter-label">Sort By</label>
              <select
                className="sb-filter-input"
                value={filters.sort_by}
                onChange={e => setFilters(f => ({ ...f, sort_by: e.target.value }))}
              >
                <option value="">Default</option>
                <option value="latest">Latest</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="rating">Rating</option>
              </select>
            </div>
          </div>
          <div className="sb-filter-actions">
            <button
              className="sb-filter-clear"
              onClick={() => setFilters({ category: '', city: '', min_price: '', max_price: '', sort_by: '' })}
            >
              Clear filters
            </button>
            <button className="sb-filter-apply" onClick={() => performSearch()}>
              Apply & Search
            </button>
          </div>
        </div>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && !showResults && (
        <ul className="sb-suggestions">
          {suggestions.map((s, i) => (
            <li key={i} className="sb-suggestion-item" onClick={() => handleSuggestionClick(s)}>
              <div className="sb-sug-img-wrap">
                {s.image && s.image !== 'string' ? (
                  <img src={s.image} alt={s.name} className="sb-sug-img" onError={e => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }} />
                ) : (
                  <div className="sb-sug-img-placeholder"><Search size={14} /></div>
                )}
              </div>
              <span className="sb-sug-name">{s.name}</span>
              <span className="sb-sug-type">{s.type}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Search results */}
      {showResults && results !== null && (
        <div className="sb-results">
          <div className="sb-results-header">
            <span className="sb-results-count">
              {results.length === 0 ? 'No results found' : `${results.length} result${results.length === 1 ? '' : 's'} for "${query}"`}
            </span>
            <button className="sb-results-close" onClick={() => setShowResults(false)}><X size={16} /></button>
          </div>
          {results.length > 0 && (
            <ul className="sb-results-list">
              {results.map(food => {
                const isAdding = addingId === food.food_id;
                const soldOut = food.available_quantity === 0;
                return (
                  <li key={food.food_id} className="sb-result-item">
                    <div className="sb-result-img-wrap">
                      <img
                        src={getFirstImageUrl(food.image_urls)}
                        alt={food.food_name}
                        className="sb-result-img"
                        onError={e => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }}
                      />
                    </div>
                    <div className="sb-result-info">
                      <p className="sb-result-name">{food.food_name}</p>
                      {food.kitchen_name && <p className="sb-result-kitchen">by {food.kitchen_name}</p>}
                      {food.description && <p className="sb-result-desc">{food.description.slice(0, 80)}{food.description.length > 80 ? '...' : ''}</p>}
                      <div className="sb-result-meta">
                        {food.category && <span className="sb-result-tag">{food.category}</span>}
                        {food.average_rating !== undefined && food.average_rating > 0 && (
                          <span className="sb-result-tag sb-result-tag--rating">★ {food.average_rating.toFixed(1)}</span>
                        )}
                        {food.available_quantity !== undefined && (
                          <span className={`sb-result-tag${soldOut ? ' sb-result-tag--out' : ''}`}>
                            {soldOut ? 'Sold out' : `${food.available_quantity} left`}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="sb-result-right">
                      <span className="sb-result-price">₹{food.price}</span>
                      {isLoggedIn && !soldOut && (
                        <button
                          className="sb-add-btn"
                          onClick={() => handleAddToCart(food)}
                          disabled={isAdding}
                          title="Add to cart"
                        >
                          {isAdding ? <Loader size={13} className="sb-add-spinner" /> : <ShoppingCart size={13} />}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
